import { useEffect, useState } from 'react';
import { StoreProductDetail, StoreVariantColor } from '../models';
import { useCartStore } from '../store/cartStore';
import { thumbnailUrlOf } from '../utils/url';
import { extractUniqueColors, extractAvailableValues, resolveVariant } from '../utils/variantResolution';

const UNLIMITED_STOCK = Number.MAX_SAFE_INTEGER;
const MIN_AVAILABLE_RATIO_FOR_MAIN_COLOR = 0.5;

export function pickDefaultColor(
  product: Pick<StoreProductDetail, 'variants' | 'images'>,
): StoreVariantColor | null {
  const colors = extractUniqueColors(product.variants);
  if (colors.length === 0) return null;

  const stats = colors
    .map((color) => {
      const own = product.variants.filter((v) => v.color?.id === color.id);
      const inStock = own.filter((v) => v.stockQuantity > 0).length;
      return { color, inStock, ratio: own.length > 0 ? inStock / own.length : 0 };
    })
    .filter((entry) => entry.inStock > 0);

  if (stats.length === 0) return colors[0];

  const best = stats.reduce((top, entry) => (entry.inStock > top.inStock ? entry : top));
  const mainImage = product.images.find((img) => img.isMain) ?? product.images[0];
  const main = stats.find((entry) => entry.color.id === mainImage?.colorId);

  if (main && (main.ratio >= MIN_AVAILABLE_RATIO_FOR_MAIN_COLOR || main.inStock >= best.inStock)) {
    return main.color;
  }
  return best.color;
}

export function useVariantSelection(product: StoreProductDetail | null) {
  const addItem = useCartStore((s) => s.addItem);
  const cartItems = useCartStore((s) => s.items);

  const [selectedColor, setSelectedColor] = useState<StoreVariantColor | null>(null);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    setSelectedValue(null);
    setQuantity(1);

    if (!product) {
      setSelectedColor(null);
      return;
    }

    setSelectedColor(pickDefaultColor(product));
  }, [product]);

  const hasVariants = (product?.variants.length ?? 0) > 0;
  const hasColors = product?.variants.some((v) => v.color !== null) ?? false;
  const uniqueColors = product && hasColors ? extractUniqueColors(product.variants) : [];
  const uniqueValues = product ? extractAvailableValues(product.variants, selectedColor) : [];
  const resolvedVariant = product ? resolveVariant(product.variants, hasColors, selectedColor, selectedValue) : null;
  const stockQuantity = hasVariants ? resolvedVariant?.stockQuantity ?? 0 : UNLIMITED_STOCK;
  const isOutOfStock = hasVariants && resolvedVariant !== null && resolvedVariant.stockQuantity === 0;
  const inCartQuantity = product
    ? cartItems.find((i) => i.productId === product.id && i.variantId === (resolvedVariant?.id ?? 0))?.quantity ?? 0
    : 0;
  const availableToAdd = Math.max(stockQuantity - inCartQuantity, 0);
  const reachedCartLimit = hasVariants && resolvedVariant !== null && !isOutOfStock && availableToAdd === 0;
  const hasPrice = (product?.hasPrice ?? false) && product?.price !== null;
  const canAddToCart =
    !!product && (!hasVariants || resolvedVariant !== null) && !isOutOfStock && !reachedCartLimit && hasPrice;

  const selectColor = (color: StoreVariantColor) => {
    setSelectedColor((prev) => (prev?.id === color.id ? null : color));
    setSelectedValue(null);
  };

  const selectValue = (value: string) => {
    setSelectedValue((prev) => (prev === value ? null : value));
  };

  useEffect(() => {
    if (availableToAdd > 0 && quantity > availableToAdd) setQuantity(availableToAdd);
  }, [availableToAdd, quantity]);

  const increaseQuantity = () => {
    if (quantity < availableToAdd) setQuantity((q) => q + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity((q) => q - 1);
  };

  const addToCart = (): 'added' | 'max_stock' | 'invalid' => {
    if (!product || product.price === null) return 'invalid';
    if (hasVariants && !resolvedVariant) return 'invalid';

    const result = addItem({
      productId: product.id,
      variantId: resolvedVariant?.id ?? 0,
      productName: product.name,
      variantLabel: resolvedVariant?.label ?? '',
      variationTypeName: resolvedVariant?.variationTypeName,
      variantSku: resolvedVariant?.sku,
      colorName: selectedColor?.name,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      imageUrl: thumbnailUrlOf(product.images[0]),
      quantity,
      stockQuantity: hasVariants ? resolvedVariant?.stockQuantity ?? 0 : UNLIMITED_STOCK,
    });

    return result === 'max_stock' ? 'max_stock' : 'added';
  };

  return {
    hasVariants,
    hasColors,
    uniqueColors,
    uniqueValues,
    selectedColor,
    selectedValue,
    quantity,
    resolvedVariant,
    stockQuantity,
    inCartQuantity,
    availableToAdd,
    reachedCartLimit,
    isOutOfStock,
    hasPrice,
    canAddToCart,
    selectColor,
    selectValue,
    increaseQuantity,
    decreaseQuantity,
    addToCart,
  };
}
